// Сквозной блок лимитов внизу бокового меню (README §1.2) и мини-шкалы
// для полосы 56. Данные — lib/limits.ts (GET /api/limits, запасной /api/usage).

import { useEffect, useState } from 'react'
import {
  fmtPctRate, fmtRate, forecastText, isEnough, leftPercent, mskClock, mskTime, useLimits, windowLeft, windowTitle,
} from '../lib/limits'
import type { LimitProvider } from '../lib/limits'
import { Chevron } from './Icon'
import './ui.css'

/** Часы МСК, обновляются раз в 15 с (минутной точности хватает). */
export function useMskClock(): string {
  const [now, setNow] = useState(() => mskClock())
  useEffect(() => {
    const timer = window.setInterval(() => setNow(mskClock()), 15_000)
    return () => window.clearInterval(timer)
  }, [])
  return now
}

export interface LimitsBlockProps {
  /** «подробнее →» — открыть Кабинет */
  onMore: () => void
}

const COMPACT_KEY = 'abx-limits-compact'

function loadCompact(): boolean {
  try {
    return localStorage.getItem(COMPACT_KEY) === '1'
  } catch {
    return false
  }
}

export function LimitsBlock({ onMore }: LimitsBlockProps) {
  const limits = useLimits()
  const clock = useMskClock()
  const providers = limits?.providers ?? null
  // Свёрнутый вид — по строке на провайдера: шкала окна 5 ч и остаток.
  const [compact, setCompact] = useState(loadCompact)
  const toggle = () => {
    setCompact((was) => {
      try {
        localStorage.setItem(COMPACT_KEY, was ? '0' : '1')
      } catch {
        // без localStorage просто не запомним
      }
      return !was
    })
  }
  return (
    <div className={`abx-limits${compact ? ' is-compact' : ''}`}>
      <button
        className="abx-limits__toggle"
        onClick={toggle}
        title={compact ? 'развернуть лимиты' : 'свернуть до окна 5 ч'}
        aria-expanded={!compact}
      >
        <span>лимиты · 5 ч</span>
        <span className="abx-limits__togglehint">
          <span className="abx-limits__msk">
            МСК <span className="abx-limits__clock">{clock}</span>
          </span>
          <Chevron open={!compact} />
        </span>
      </button>
      {providers === null ? (
        <div className="abx-limits__row abx-limits__muted">лимиты загружаются…</div>
      ) : compact ? (
        providers.map((p) => <CompactLimits key={p.id} provider={p} />)
      ) : (
        providers.map((p) => <ProviderLimits key={p.id} provider={p} />)
      )}
      {compact ? null : (
        <div className="abx-limits__foot">
          <span />
          <button className="abx-limits__more" onClick={onMore}>подробнее →</button>
        </div>
      )}
    </div>
  )
}

/** Свёрнутая строка: имя · шкала окна 5 ч · остаток. */
function CompactLimits({ provider: p }: { provider: LimitProvider }) {
  const left = leftPercent(p)
  const ok = isEnough(p)
  return (
    <div className="abx-limits__compact" title={forecastText(p)}>
      <span className="abx-limits__name">{p.title}</span>
      <span className="abx-limits__bar">
        {left !== null && p.available ? (
          <span className={`abx-limits__fill ${ok ? 'is-ok' : 'is-warn'}`} style={{ width: `${left}%` }} />
        ) : null}
      </span>
      <span className={`abx-limits__pct${ok ? '' : ' tone-warn'}`}>
        {left !== null && p.available ? `${left}%` : '—'}
      </span>
    </div>
  )
}

function ProviderLimits({ provider: p }: { provider: LimitProvider }) {
  const left = leftPercent(p)
  if (!p.available || left === null) {
    return (
      <div className="abx-limits__prov">
        <div className="abx-limits__row">
          <span className="abx-limits__name">{p.title}</span>
          <span className="abx-limits__muted">нет данных</span>
        </div>
        <div className="abx-limits__bar" />
        {p.reason && p.reason !== 'нет данных' ? (
          <div className="abx-limits__forecast abx-limits__muted">{p.reason}</div>
        ) : null}
      </div>
    )
  }
  const ok = isEnough(p)
  const pct = fmtPctRate(p)
  return (
    <div className="abx-limits__prov">
      <div className="abx-limits__row">
        <span className="abx-limits__name">{p.title}</span>
        <span className="abx-limits__rate">
          {fmtRate(p.tokensPerMin)} ток/мин{pct ? ` · ${pct}` : ''}
        </span>
      </div>
      {p.windows.map((w) => {
        const wl = windowLeft(w)
        // жёлтым — только окно 5 ч, если при текущем расходе его не хватит;
        // неделя справочно и прогнозом не красится
        const warn = !ok && w.kind === '5h'
        return (
          <div key={w.kind} className="abx-limits__win">
            <div className="abx-limits__row">
              <span className="abx-limits__muted">{windowTitle(w)}</span>
              <span className="abx-limits__pct">осталось {wl}%</span>
            </div>
            <div className="abx-limits__bar">
              <div className={`abx-limits__fill ${warn ? 'is-warn' : 'is-ok'}`} style={{ width: `${wl}%` }} />
            </div>
            <div className="abx-limits__rate">
              <span>{w.resetsAt ? `сброс ${mskTime(w.resetsAt)}` : 'окно не начато'}</span>
            </div>
          </div>
        )
      })}
      <div className={`abx-limits__forecast ${ok ? 'is-ok' : 'is-warn'}`}>{forecastText(p)}</div>
    </div>
  )
}

/** Мини-шкалы полосы 56: две полосы 5×36, заливка снизу — остаток. */
export function RailLimits({ onClick }: { onClick: () => void }) {
  const limits = useLimits()
  const providers = limits?.providers ?? []
  const tip = providers
    .map((p) => `${p.title}: ${leftPercent(p) === null ? 'нет данных' : `5 ч — осталось ${leftPercent(p)}%`}`)
    .join(' · ')
  return (
    <button className="abx-rail__limits" title={`лимиты и расход — ${tip}`} onClick={onClick}>
      {providers.map((p) => {
        const left = leftPercent(p) ?? 0
        return (
          <span key={p.id} className="abx-rail__bar">
            <span className={`abx-rail__fill ${isEnough(p) ? 'is-ok' : 'is-warn'}`} style={{ height: `${left}%` }} />
          </span>
        )
      })}
    </button>
  )
}
